import React, { useMemo, useRef, useState } from "react";
import Box from "@material-ui/core/Box";
import Checkbox from "@material-ui/core/Checkbox";
import CircularProgress from "@material-ui/core/CircularProgress";
import FormControl from "@material-ui/core/FormControl";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormLabel from "@material-ui/core/FormLabel";
import MenuItem from "@material-ui/core/MenuItem";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import EmailOutlinedIcon from "@material-ui/icons/EmailOutlined";
import PhoneOutlinedIcon from "@material-ui/icons/PhoneOutlined";
import ScheduleOutlinedIcon from "@material-ui/icons/ScheduleOutlined";
import * as emailjs from "emailjs-com";

import Button from "components/CustomButtons/Button.js";
import CustomInput from "components/CustomInput/CustomInput.js";
import GlobalMessage from "components/GlobalMessage";
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import { APP_STRINGS } from "constants/appStrings";

const useStyles = makeStyles((theme) => ({
  section: {
    width: "min(1240px, calc(100% - 48px))",
    margin: "0 auto",
    padding: theme.spacing(6, 0, 8),
    [theme.breakpoints.down("sm")]: {
      width: "min(100% - 32px, 1240px)",
      paddingBottom: theme.spacing(4),
    },
  },
  introCard: {
    height: "100%",
    padding: theme.spacing(4),
    borderRadius: 34,
    background: "linear-gradient(135deg, #6d3d4d 0%, #8c5366 100%)",
    color: "#f7f2ea",
    boxShadow: "0 34px 70px rgba(98, 54, 69, 0.16)",
  },
  badge: {
    display: "inline-flex",
    padding: theme.spacing(1, 2),
    borderRadius: 999,
    background: "rgba(255,255,255,0.08)",
    color: "#e8d3ae",
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
  },
  title: {
    margin: theme.spacing(2, 0),
    fontFamily: '"Cormorant Garamond", "Times New Roman", serif',
    fontSize: "clamp(2.5rem, 4vw, 3.8rem)",
    lineHeight: 0.96,
    color: "#fff7ed",
  },
  text: {
    fontSize: 17,
    lineHeight: 1.9,
    color: "rgba(247, 242, 234, 0.78)",
  },
  contactPoint: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2.5),
    borderRadius: 24,
    background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.1)",
  },
  contactLabel: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    color: "#fff2dc",
    fontWeight: 700,
    marginBottom: theme.spacing(1),
  },
  contactValue: {
    color: "rgba(247, 242, 234, 0.74)",
    lineHeight: 1.8,
  },
  formShell: {
    padding: theme.spacing(4),
    borderRadius: 34,
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(249,245,239,0.92) 100%)",
    border: "1px solid rgba(31, 38, 50, 0.08)",
    boxShadow: "0 30px 70px rgba(22, 29, 40, 0.08)",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(3),
    },
  },
  formTitle: {
    fontFamily: '"Cormorant Garamond", "Times New Roman", serif',
    fontSize: 34,
    color: "#5d3342",
    marginBottom: theme.spacing(1),
  },
  formText: {
    color: "#5f6775",
    lineHeight: 1.8,
    marginBottom: theme.spacing(3),
  },
  helper: {
    fontSize: 12,
    color: "#7b8391",
    marginTop: theme.spacing(0.5),
  },
  errorText: {
    fontSize: 12,
    color: theme.palette.error.main,
    marginTop: theme.spacing(0.5),
  },
  actions: {
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "center",
    flexWrap: "wrap",
  },
  subtle: {
    fontSize: 12,
    color: "#7b8391",
    lineHeight: 1.7,
  },
  counter: {
    marginLeft: "auto",
    fontSize: 12,
    color: "#7b8391",
  },
  lgpd: {
    marginTop: theme.spacing(1),
  },
  honeypot: {
    position: "absolute",
    left: "-9999px",
    width: 1,
    height: 1,
    overflow: "hidden",
  },
  submitButton: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1.1, 3),
    borderRadius: 999,
    background: "linear-gradient(135deg, #8d4d61 0%, #a75e74 100%)",
    color: "#fff8ef",
  },
  clearButton: {
    color: "#4d5664",
  },
}));

const MSG_MIN_LEN = 20;
const MSG_MAX_LEN = 2000;

const normalizePhone = (v) => v.replace(/\D+/g, "").slice(0, 15);
const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
const hasWords = (v) => v.trim().split(/\s+/).length >= 2;

export default function ContactSection() {
  const classes = useStyles();

  const initialForm = useMemo(
    () => ({
      name: "",
      email: "",
      phone: "",
      company: "",
      legalArea: "",
      subject: "",
      motive: "",
      preferredChannel: "email",
      message: "",
      acceptLGPD: false,
      allowNewsletter: false,
      jobTitle: "",
    }),
    []
  );

  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [feedback, setFeedback] = useState({ message: "", severity: "" });
  const [loading, setLoading] = useState(false);
  const firstErrorRef = useRef(null);

  const remaining = MSG_MAX_LEN - (form.message?.length || 0);

  const setField = (id, value) => {
    setForm((prev) => ({ ...prev, [id]: value }));
    setErrors((prev) => ({ ...prev, [id]: undefined }));
  };

  const handleChange = (e) => {
    const { id, value, name, type, checked } = e.target;
    const key = id || name;

    if (key === "phone") {
      return setField("phone", normalizePhone(value));
    }

    if (type === "checkbox") {
      return setField(key, checked);
    }

    setField(key, value);
  };

  const validate = () => {
    const e = {};

    if (!hasWords(form.name)) e.name = APP_STRINGS.contact.validation.name;
    if (!isEmail(form.email)) e.email = APP_STRINGS.contact.validation.email;
    if (!form.phone || normalizePhone(form.phone).length < 10) {
      e.phone = APP_STRINGS.contact.validation.phone;
    }
    if (!form.legalArea) {
      e.legalArea = APP_STRINGS.contact.validation.legalArea;
    }
    if (!form.subject) e.subject = APP_STRINGS.contact.validation.subject;
    if (!form.motive) e.motive = APP_STRINGS.contact.validation.motive;

    const len = (form.message || "").trim().length;
    if (len < MSG_MIN_LEN) {
      e.message = APP_STRINGS.contact.validation.messageMin(MSG_MIN_LEN);
    } else if (len > MSG_MAX_LEN) {
      e.message = APP_STRINGS.contact.validation.messageMax(MSG_MAX_LEN);
    }

    if (!form.acceptLGPD) {
      e.acceptLGPD = APP_STRINGS.contact.validation.lgpd;
    }

    return e;
  };

  const focusFirstError = (e) => {
    const id = Object.keys(e)[0];
    if (!id) return;
    const el =
      document.getElementById(id) ||
      (firstErrorRef.current && firstErrorRef.current.querySelector(`#${id}`));
    if (el && typeof el.focus === "function") el.focus();
  };

  const resetForm = () => {
    setForm(initialForm);
    setErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (form.jobTitle) return;

    const v = validate();
    setErrors(v);

    if (Object.keys(v).length) {
      setFeedback({
        message: APP_STRINGS.contact.feedback.review,
        severity: "error",
      });
      setSnackbarOpen(true);
      focusFirstError(v);
      return;
    }

    setLoading(true);
    try {
      const templateParams = {
        to_name: APP_STRINGS.brand.fullName,
        from_name: form.name,
        reply_to: form.email,
        phone: form.phone,
        company: form.company || APP_STRINGS.contact.feedback.companyFallback,
        legal_area:
          APP_STRINGS.contact.legalAreas.find((area) => area.value === form.legalArea)
            ?.label || form.legalArea,
        subject:
          APP_STRINGS.contact.subjects.find((s) => s.value === form.subject)?.label ||
          form.subject,
        motive:
          APP_STRINGS.contact.motives.find((m) => m.value === form.motive)?.label ||
          form.motive,
        preferred_channel:
          APP_STRINGS.contact.preferredChannels.find(
            (p) => p.value === form.preferredChannel
          )?.label || form.preferredChannel,
        allow_newsletter: form.allowNewsletter
          ? APP_STRINGS.contact.feedback.yes
          : APP_STRINGS.contact.feedback.no,
        message_html: form.message,
      };

      const SERVICE_ID = "gmail";
      const TEMPLATE_ID = "template_eWZVwsbZ";
      const USER_ID = "bEMzl3KN6UD5_fbjD";

      const request = await emailjs.send(
        SERVICE_ID,
        TEMPLATE_ID,
        templateParams,
        USER_ID
      );

      if (request?.status === 200) {
        setFeedback({
          message: APP_STRINGS.contact.feedback.success,
          severity: "success",
        });
        setSnackbarOpen(true);
        resetForm();
      } else {
        throw new Error(`Falha no envio: ${request?.status || "desconhecido"}`);
      }
    } catch (err) {
      setFeedback({
        message: APP_STRINGS.contact.feedback.error,
        severity: "error",
      });
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={classes.section} ref={firstErrorRef}>
      <GridContainer spacing={4}>
        <GridItem xs={12} md={5}>
          <div className={classes.introCard}>
            <span className={classes.badge}>{APP_STRINGS.contact.badge}</span>
            <Typography component="h2" className={classes.title}>
              {APP_STRINGS.contact.title}
            </Typography>
            <Typography component="p" className={classes.text}>
              {APP_STRINGS.contact.text}
            </Typography>

            {APP_STRINGS.contact.points.map((point, index) => {
              const iconMap = [
                <PhoneOutlinedIcon fontSize="small" />,
                <EmailOutlinedIcon fontSize="small" />,
                <ScheduleOutlinedIcon fontSize="small" />,
              ];

              return (
                <div className={classes.contactPoint} key={point.title}>
                  <div className={classes.contactLabel}>
                    {iconMap[index]}
                    {point.title}
                  </div>
                  <div className={classes.contactValue}>{point.value}</div>
                </div>
              );
            })}
          </div>
        </GridItem>

        <GridItem xs={12} md={7}>
          <div className={classes.formShell}>
            <Typography component="h3" className={classes.formTitle}>
              {APP_STRINGS.contact.formTitle}
            </Typography>
            <Typography component="p" className={classes.formText}>
              {APP_STRINGS.contact.formText}
            </Typography>

            <form
              id="contact-form"
              onSubmit={handleSubmit}
              method="POST"
              noValidate
              aria-labelledby="contact-heading"
            >
              <div className={classes.honeypot} aria-hidden="true">
                <label htmlFor="jobTitle">
                  {APP_STRINGS.contact.fields.honeypot}
                </label>
                <input
                  type="text"
                  id="jobTitle"
                  name="jobTitle"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.jobTitle}
                  onChange={handleChange}
                />
              </div>

              <GridContainer>
                <GridItem xs={12} sm={6}>
                  <CustomInput
                    labelText={APP_STRINGS.contact.fields.name}
                    id="name"
                    value={form.name}
                    onChange={handleChange}
                    formControlProps={{ fullWidth: true }}
                    inputProps={{
                      required: true,
                      "aria-invalid": !!errors.name,
                      "aria-describedby": errors.name ? "err-name" : undefined,
                      autoComplete: "name",
                    }}
                  />
                  {errors.name ? (
                    <div id="err-name" className={classes.errorText}>{errors.name}</div>
                  ) : (
                    <div className={classes.helper}>
                      {APP_STRINGS.contact.fields.nameHelper}
                    </div>
                  )}
                </GridItem>

                <GridItem xs={12} sm={6}>
                  <CustomInput
                    labelText={APP_STRINGS.contact.fields.email}
                    id="email"
                    value={form.email}
                    onChange={handleChange}
                    formControlProps={{ fullWidth: true }}
                    inputProps={{
                      required: true,
                      type: "email",
                      "aria-invalid": !!errors.email,
                      "aria-describedby": errors.email ? "err-email" : undefined,
                      autoComplete: "email",
                    }}
                  />
                  {errors.email ? (
                    <div id="err-email" className={classes.errorText}>{errors.email}</div>
                  ) : (
                    <div className={classes.helper}>
                      {APP_STRINGS.contact.fields.emailHelper}
                    </div>
                  )}
                </GridItem>

                <GridItem xs={12} sm={6}>
                  <CustomInput
                    labelText={APP_STRINGS.contact.fields.phone}
                    id="phone"
                    value={form.phone}
                    onChange={handleChange}
                    formControlProps={{ fullWidth: true }}
                    inputProps={{
                      required: true,
                      inputMode: "tel",
                      "aria-invalid": !!errors.phone,
                      "aria-describedby": errors.phone ? "err-phone" : undefined,
                      autoComplete: "tel",
                    }}
                  />
                  {errors.phone ? (
                    <div id="err-phone" className={classes.errorText}>{errors.phone}</div>
                  ) : (
                    <div className={classes.helper}>
                      {APP_STRINGS.contact.fields.phoneHelper}
                    </div>
                  )}
                </GridItem>

                <GridItem xs={12} sm={6}>
                  <CustomInput
                    labelText={APP_STRINGS.contact.fields.company}
                    id="company"
                    value={form.company}
                    onChange={handleChange}
                    formControlProps={{ fullWidth: true }}
                    inputProps={{ autoComplete: "organization" }}
                  />
                  <div className={classes.helper}>
                    {APP_STRINGS.contact.fields.companyHelper}
                  </div>
                </GridItem>

                <GridItem xs={12} sm={6}>
                  <CustomInput
                    labelText={APP_STRINGS.contact.fields.legalArea}
                    id="legalArea"
                    value={form.legalArea}
                    onChange={handleChange}
                    formControlProps={{ fullWidth: true }}
                    inputProps={{
                      select: true,
                      "aria-invalid": !!errors.legalArea,
                      "aria-describedby": errors.legalArea ? "err-legalArea" : undefined,
                    }}
                  >
                    {APP_STRINGS.contact.legalAreas.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </CustomInput>
                  {errors.legalArea ? (
                    <div id="err-legalArea" className={classes.errorText}>
                      {errors.legalArea}
                    </div>
                  ) : (
                    <div className={classes.helper}>
                      {APP_STRINGS.contact.fields.legalAreaHelper}
                    </div>
                  )}
                </GridItem>

                <GridItem xs={12} sm={6}>
                  <CustomInput
                    labelText={APP_STRINGS.contact.fields.subject}
                    id="subject"
                    value={form.subject}
                    onChange={handleChange}
                    formControlProps={{ fullWidth: true }}
                    inputProps={{
                      select: true,
                      "aria-invalid": !!errors.subject,
                      "aria-describedby": errors.subject ? "err-subject" : undefined,
                    }}
                  >
                    {APP_STRINGS.contact.subjects.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </CustomInput>
                  {errors.subject ? (
                    <div id="err-subject" className={classes.errorText}>{errors.subject}</div>
                  ) : (
                    <div className={classes.helper}>
                      {APP_STRINGS.contact.fields.subjectHelper}
                    </div>
                  )}
                </GridItem>

                <GridItem xs={12} sm={6}>
                  <CustomInput
                    labelText={APP_STRINGS.contact.fields.motive}
                    id="motive"
                    value={form.motive}
                    onChange={handleChange}
                    formControlProps={{ fullWidth: true }}
                    inputProps={{
                      select: true,
                      "aria-invalid": !!errors.motive,
                      "aria-describedby": errors.motive ? "err-motive" : undefined,
                    }}
                  >
                    {APP_STRINGS.contact.motives.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </CustomInput>
                  {errors.motive ? (
                    <div id="err-motive" className={classes.errorText}>{errors.motive}</div>
                  ) : (
                    <div className={classes.helper}>
                      {APP_STRINGS.contact.fields.motiveHelper}
                    </div>
                  )}
                </GridItem>

                <GridItem xs={12}>
                  <FormControl component="fieldset" fullWidth>
                    <FormLabel component="legend">
                      {APP_STRINGS.contact.fields.preferredChannel}
                    </FormLabel>
                    <RadioGroup
                      aria-label="Canal preferido"
                      name="preferredChannel"
                      value={form.preferredChannel}
                      onChange={handleChange}
                      row
                    >
                      {APP_STRINGS.contact.preferredChannels.map((c) => (
                        <FormControlLabel
                          key={c.value}
                          value={c.value}
                          control={<Radio color="primary" />}
                          label={c.label}
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>
                </GridItem>

                <GridItem xs={12}>
                  <CustomInput
                    labelText={`${APP_STRINGS.contact.fields.message} (min. ${MSG_MIN_LEN} - max. ${MSG_MAX_LEN})`}
                    id="message"
                    value={form.message}
                    onChange={handleChange}
                    formControlProps={{ fullWidth: true }}
                    inputProps={{
                      multiline: true,
                      rows: 6,
                      "aria-invalid": !!errors.message,
                      "aria-describedby": errors.message ? "err-message" : undefined,
                      inputProps: { maxLength: MSG_MAX_LEN },
                    }}
                  />
                  <Box display="flex" alignItems="center" flexWrap="wrap">
                    {errors.message ? (
                      <div id="err-message" className={classes.errorText}>{errors.message}</div>
                    ) : (
                      <div className={classes.helper}>
                        {APP_STRINGS.contact.fields.messageHelper}
                      </div>
                    )}
                    <span className={classes.counter}>{remaining} caracteres</span>
                  </Box>
                </GridItem>

                <GridItem xs={12}>
                  <FormControlLabel
                    className={classes.lgpd}
                    control={
                      <Checkbox
                        id="acceptLGPD"
                        name="acceptLGPD"
                        color="primary"
                        checked={form.acceptLGPD}
                        onChange={handleChange}
                        inputProps={{
                          "aria-invalid": !!errors.acceptLGPD,
                          "aria-describedby": errors.acceptLGPD ? "err-lgpd" : undefined,
                        }}
                      />
                    }
                    label={
                      <span>
                        {APP_STRINGS.contact.fields.lgpdLabelStart}{" "}
                        <a href="/privacidade" target="_blank" rel="noreferrer">
                          {APP_STRINGS.contact.fields.lgpdPolicy}
                        </a>
                        .
                      </span>
                    }
                  />
                  {errors.acceptLGPD && (
                    <div id="err-lgpd" className={classes.errorText}>{errors.acceptLGPD}</div>
                  )}

                  <FormControlLabel
                    control={
                      <Checkbox
                        id="allowNewsletter"
                        name="allowNewsletter"
                        color="primary"
                        checked={form.allowNewsletter}
                        onChange={handleChange}
                      />
                    }
                    label={APP_STRINGS.contact.fields.newsletter}
                  />

                  <div className={classes.subtle}>
                    {APP_STRINGS.contact.fields.subtle}
                  </div>
                </GridItem>

                <GridItem xs={12}>
                  <Box className={classes.actions}>
                    <Button
                      type="submit"
                      color="primary"
                      disabled={loading}
                      aria-label={APP_STRINGS.contact.feedback.submitAria}
                      className={classes.submitButton}
                    >
                      {loading ? (
                        <CircularProgress size={20} />
                      ) : (
                        APP_STRINGS.contact.fields.submit
                      )}
                    </Button>
                    <Button
                      type="button"
                      color="transparent"
                      simple
                      onClick={() => resetForm()}
                      disabled={loading}
                      className={classes.clearButton}
                    >
                      {APP_STRINGS.contact.fields.clear}
                    </Button>
                  </Box>
                </GridItem>
              </GridContainer>
            </form>
          </div>
        </GridItem>
      </GridContainer>

      <GlobalMessage
        onClick={() => setSnackbarOpen(false)}
        snackbarOpen={snackbarOpen}
        message={feedback}
      />
    </div>
  );
}
